import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS, useReadCachedListItem } from '@/shared/api';
import type { TableViewSnapshot } from '@/entities/settings/models/types';
import { createSystemPreset, deleteSystemPreset, fetchSystemPresets, updateSystemPreset } from './api';
import type { SystemPresetListResponse } from './types';

/** Query-ключи системных пресетов */
export const systemPresetKeys = {
  all: [API_TAGS.SYSTEM_PRESETS] as const,
  list: (tableName?: string) => [...systemPresetKeys.all, 'list', tableName] as const,
};

/**
 * Системные пресеты таблицы или системные дашборды. Набор общий для всех пользователей, поэтому
 * кэш один на приложение и доступен любому авторизованному пользователю.
 *
 * Пустое имя таблицы приходит из разделов, где пресетов нет вовсе (выборки): запрос там не
 * нужен, а без имени таблицы сервер отдал бы пресеты всех таблиц разом.
 */
export const useSystemPresets = <TSettings = TableViewSnapshot>(tableName: string) =>
  useQuery({
    queryKey: systemPresetKeys.list(tableName),
    queryFn: () => fetchSystemPresets<TSettings>(tableName),
    enabled: Boolean(tableName),
  });

/** Чтение системного пресета из последнего полученного списка в момент вызова ({@link useReadCachedListItem}). */
export const useReadCachedSystemPreset = <TSettings = TableViewSnapshot>(tableName: string) =>
  useReadCachedListItem<SystemPresetListResponse<TSettings>, string>(systemPresetKeys.list, tableName);

/** Создание системного пресета; по успеху список перечитывается до колбэков вызывающего */
export const useCreateSystemPreset = <TSettings = TableViewSnapshot>() => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSystemPreset<TSettings>,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: systemPresetKeys.all }),
  });
};

/**
 * Обновление названия, содержимого или закрепления системного пресета; по успеху список
 * перечитывается до колбэков вызывающего
 */
export const useUpdateSystemPreset = <TSettings = TableViewSnapshot>() => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSystemPreset<TSettings>,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: systemPresetKeys.all }),
  });
};

/** Удаление системного пресета */
export const useDeleteSystemPreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSystemPreset,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: systemPresetKeys.all }),
  });
};
